import { useDialog } from '@globals/dialog/useDialogStore'
import { ConfirmDialog } from '@shared/dialog/ConfirmDialog'
import { T } from '@transifex/react'
import { FC, useCallback, useEffect, useRef } from 'react'
import { useBlocker } from 'react-router'

type PendingClose = { type: 'nav' | 'modal'; doClose?: () => void }

type DirtyFormBlockerProps = {
  isFormDirty: boolean
  isSubmitSuccessful: boolean
  onDiscardData?: () => void
  onKeepData?: () => Promise<void>
  dialogKey: string
  onRequestClose?: (attemptClose: (doClose: () => void) => boolean) => void
  /** When false, navigation is not blocked and the dialog will only open via the modal-close path (onRequestClose). Default true. */
  blockNavigation?: boolean
}

const DirtyFormBlocker: FC<DirtyFormBlockerProps> = ({
  isFormDirty,
  isSubmitSuccessful,
  onDiscardData,
  onKeepData,
  dialogKey,
  onRequestClose,
  blockNavigation = true,
}) => {
  const { openDialog, closeDialog } = useDialog(dialogKey)
  const pendingCloseRef = useRef<PendingClose | null>(null)
  const isFormDirtyRef = useRef(isFormDirty)
  isFormDirtyRef.current = isFormDirty

  const shouldBlock = blockNavigation && isFormDirty && !isSubmitSuccessful
  const navBlocker = useBlocker(shouldBlock)
  const isBlockedState = navBlocker.state === 'blocked'
  const isUnblockedState = navBlocker.state === 'unblocked'

  const attemptClose = useCallback(
    (doClose: () => void): boolean => {
      if (!isFormDirtyRef.current) {
        doClose()
        return true
      }
      pendingCloseRef.current = { type: 'modal', doClose }
      openDialog()
      return false
    },
    [openDialog],
  )

  useEffect(() => {
    if (isBlockedState) {
      // NOTE: Router can stay blocked after shouldBlock becomes false (e.g. save succeeded or
      // blockNavigation disabled) while navigation is still pending. Proceed to release it, i.e.
      // do not call proceed() when state is unblocked (can cause Error: Invalid blocker state transition).
      if (!shouldBlock) {
        pendingCloseRef.current = null
        closeDialog()
        navBlocker.proceed?.()
        return
      }
      pendingCloseRef.current = { type: 'nav' }
      openDialog()
      return
    }
    if (isUnblockedState) {
      closeDialog()
    }
  }, [navBlocker, shouldBlock, closeDialog, openDialog, isBlockedState, isUnblockedState])

  useEffect(() => {
    onRequestClose?.(attemptClose)
  }, [onRequestClose, attemptClose])

  const handleClose = () => {
    navBlocker?.reset?.()
    pendingCloseRef.current = null
  }

  const handleKeepData = () => {
    !!onKeepData && onKeepData()
    handleClose()
    closeDialog()
  }

  const handleDiscard = () => {
    !!onDiscardData && onDiscardData()
    closeDialog()
    const pending = pendingCloseRef.current
    pendingCloseRef.current = null
    if (pending?.type === 'modal') {
      pending.doClose?.()
    } else if (isBlockedState) {
      navBlocker.proceed?.()
    }
  }

  return (
    <ConfirmDialog
      dialogKey={dialogKey}
      title={<T _str="Are you sure you want to leave?" />}
      onClose={handleClose}
      onConfirm={handleKeepData}
      onCancel={handleDiscard}
      cancelButtonColor="error"
      cancelButtonText={<T _str="Leave" />}
      confirmButtonText={<T _str="Cancel" />}>
      <span>
        <T _str="Changes you've made won't be saved." />
      </span>
    </ConfirmDialog>
  )
}

export default DirtyFormBlocker
